<template>
  <Dialog
    :open="modelValue"
    as="div"
    class="relative z-50 text-gray-700"
    @close="emit('update:modelValue', false)"
  >
    <div
      class="fixed inset-0 bg-black/30"
      aria-hidden="true"
    />
    <div class="fixed inset-0 flex items-center justify-center p-4">
      <DialogPanel class="flex h-2/3 w-full max-w-2xl flex-col rounded bg-gray-50 md:h-4/5">
        <div class="flex-none rounded-t border-b border-gray-300 bg-gray-50 p-2 md:p-4">
          <button
            class="mb-2 ml-auto flex focus:outline-none"
            @click="emit('update:modelValue', false)"
          >
            <XMarkIcon class="size-6 fill-gray-700" />
          </button>
          <DialogTitle class="mb-3 text-center font-serif text-xl text-gray-700 md:text-3xl">
            Agregar productos al descuento
          </DialogTitle>
          <div class="mb-3 rounded-md bg-blue-50 p-3">
            <p class="text-sm text-blue-800 md:text-base">
              <strong>{{ discount.name }}</strong> - {{ discount.makerDiscount }}% de descuento
            </p>
            <p class="text-xs text-blue-600">
              Válido desde {{ timeToDate(discount.startDate) }} hasta {{ timeToDate(discount.endDate) }}
            </p>
          </div>
        </div>
        <div class="flex-1 overflow-y-auto">
          <DialogDescription class="px-2 py-4 text-sm font-light text-gray-700 md:px-4 md:text-base">
            <form
              class="flex h-full flex-col"
              @submit="onSubmit"
            >
              <div class="mb-4">
                <label class="mb-2 text-sm md:text-base">Seleccionar productos</label>
                <lokal-loading
                  v-if="productQueryIsLoading"
                  size="xs"
                  :loading="productQueryIsLoading"
                  class="mb-4 mt-2"
                />
                <lokal-generic-combobox
                  v-else
                  v-model="selectedProducts"
                  class="mb-2"
                  :options="products || []"
                  :get-option-label="(option) => option.name"
                  :get-option-key="(option) => `${option.id}`"
                  :placeholder="'Buscar productos'"
                  :multiple="true"
                  :options-label="'Seleccionar productos para descuento'"
                >
                  <template
                    v-for="product in products"
                    :key="product.id"
                    #[`custom-option-${product.id}`]="{ active, selected, option, getOptionLabel }"
                  >
                    <li
                      class="flex cursor-pointer items-center space-x-2 p-1.5 transition-colors sm:space-x-2 sm:p-2"
                      :class="{ 'bg-lk-green text-white': active || selected }"
                    >
                      <CheckIcon
                        class="size-3 shrink-0 sm:size-4"
                        :class="{ hidden: !selected, 'text-white': selected }"
                      />
                      <img
                        v-if="product.images?.[0]?.derivatives?.jpgSm?.url"
                        :src="product.images[0].derivatives.jpgSm.url"
                        class="size-6 shrink-0 rounded object-cover sm:size-8"
                      />
                      <div
                        v-else
                        class="flex size-6 shrink-0 items-center justify-center rounded bg-gray-100 sm:size-8"
                      >
                        <span class="text-xs text-gray-400">Sin imagen</span>
                      </div>
                      <div class="min-w-0 flex-1">
                        <div class="truncate text-xs font-medium sm:text-sm">{{ getOptionLabel(option) }}</div>
                        <div class="text-xs text-gray-400">SKU: {{ option.sku }}</div>
                      </div>
                    </li>
                  </template>
                </lokal-generic-combobox>
                <div
                  v-if="selectedProducts.length > 0"
                  class="mt-3"
                >
                  <p class="mb-2 text-sm font-medium md:text-base">
                    Productos seleccionados ({{ selectedProducts.length }})
                  </p>
                  <div
                    class="max-h-32 space-y-2 overflow-y-auto rounded-md border bg-white p-2 md:max-h-60 lg:max-h-80"
                  >
                    <div
                      v-for="product in selectedProducts"
                      :key="product.id"
                      class="flex items-center justify-between rounded bg-gray-50 p-2"
                    >
                      <div class="flex items-center space-x-2">
                        <img
                          v-if="product.images?.[0]?.derivatives?.jpgSm?.url"
                          :src="product.images[0].derivatives.jpgSm.url"
                          class="size-6 rounded object-cover"
                        />
                        <div>
                          <p class="text-sm font-medium">{{ product.name }}</p>
                          <p class="text-xs text-gray-500">SKU: {{ product.sku }}</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        class="text-red-500 hover:text-red-700"
                        @click="removeProduct(product.id)"
                      >
                        <XMarkIcon class="size-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </DialogDescription>
        </div>
        <div class="flex-none rounded-b border-t border-gray-300 bg-gray-50 p-2 md:p-4">
          <div class="flex justify-between gap-2">
            <button
              type="button"
              class="w-full rounded-md border border-gray-300 bg-white p-2 text-sm font-normal text-gray-700 hover:bg-gray-50 md:text-base"
              @click="emit('update:modelValue', false)"
            >
              Cancelar
            </button>
            <lokal-loading
              v-if="isLoading"
              size="xs"
              :loading="isLoading"
              class="w-full justify-center"
            />
            <button
              v-else
              type="submit"
              class="w-full rounded-md border border-lk-green bg-white p-2 text-sm font-normal text-lk-green hover:bg-lk-green hover:text-white md:text-base"
              :disabled="selectedProducts.length === 0"
              :class="{ 'cursor-not-allowed opacity-50': selectedProducts.length === 0 }"
              @click="onSubmit"
            >
              Agregar productos
            </button>
          </div>
        </div>
      </DialogPanel>
    </div>
  </Dialog>
</template>

<script lang="ts" setup>
import { XMarkIcon, CheckIcon } from '@heroicons/vue/24/solid';
import { ref, computed } from 'vue';
import { Dialog, DialogPanel, DialogTitle, DialogDescription } from '@headlessui/vue';
import { useMutation } from 'vue-query';
import productDiscountsApi from 'api/product_discounts';
import useProductsQuery from './queries/products-query';
import lokalGenericCombobox from './shared/lokal-generic-combobox.vue';
import lokalLoading from './shared/lokal-loading.vue';
import dateTools from './use/date-tools';

interface MakersDiscountAddProductsDialogProps {
  modelValue: boolean;
  discount: Discount;
  makerId?: number;
}

const props = withDefaults(defineProps<MakersDiscountAddProductsDialogProps>(), {
  modelValue: false,
  makerId: undefined,
});

const emit = defineEmits<{
  'reload-discounts': [];
  'update:modelValue': [value: boolean];
}>();

const { timeToDate } = dateTools();

const selectedProducts = ref<Product[]>([]);
const currentPage = ref(1);
const queryParams = computed(() => `maker_id=${props.makerId}`);
const enabled = computed(() => !!props.makerId && props.modelValue);

const { productQueryIsLoading, products } = useProductsQuery(
  currentPage,
  queryParams,
  enabled,
  computed(() => 100),
);

function emitEvents() {
  emit('update:modelValue', false);
  emit('reload-discounts');
}

function removeProduct(productId: number) {
  selectedProducts.value = selectedProducts.value.filter((product) => product.id !== productId);
}

const addProductsMutation = useMutation(
  () =>
    productDiscountsApi.bulkCreate(
      props.discount.id,
      selectedProducts.value.map((product) => product.id),
    ),
  {
    onSuccess: () => {
      emitEvents();
      selectedProducts.value = [];
    },
  },
);

const isLoading = computed(() => addProductsMutation.isLoading.value);

function onSubmit(event: Event) {
  event.preventDefault();
  if (selectedProducts.value.length > 0) {
    addProductsMutation.mutate();
  }
}
</script>
